import { Line, ILineUpdate } from './Line'
import { MultiMsg, Font } from 'NSwag/ServerClient/Client.nswag';
import { DefaultJustification, PhaseAlignment, LineAlignment } from 'DataLibrary/Dms/MultiMsgConstants';
import { ISignDimensions } from './MultiUtils';

interface IPhaseArgs {
    onChange: (update: IPhaseUpdate) => void;
    signDimensions: ISignDimensions;
    index: number,
    font?: Font | null
}

export interface IPhaseUpdate {
    phaseIndex: number,
    overflow?: string,
    underflow?: number | null,
    dequeuedChars? : string | null,
    setHardPhaseBreak?: boolean,
    isEmpty?: boolean
}

/**
 * An object representation of a DMS editor phase.
 */
export class Phase {

    lines: Line[];

    onChange: (update: IPhaseUpdate) => void;

    signDimensions: ISignDimensions;

    lineCount: number;

    colCount: number;

    maxWidth: number;

    index: number;

    font?: Font | null;

    onTime: number;

    offTime: number;

    hardPhaseBreak: boolean;

    alignment: PhaseAlignment;

    graphicId: number;

    constructor({ onChange, signDimensions, index, font }: IPhaseArgs) {
        this.signDimensions = signDimensions;
        this.lineCount = signDimensions.lines;
        this.colCount = signDimensions.col;
        this.maxWidth = signDimensions.width;
        this.onChange = onChange;
        this.index = index;
        this.font = font;
        this.hardPhaseBreak = false;
        this.onTime = 30;
        this.offTime = 0;
        this.alignment = PhaseAlignment.Middle;
        this.graphicId = -1;

        this.lines = [new Line({
            onChange: this.handleLineUpdate,
            maxWidth: this.maxWidth,
            font: this.font,
            index: 0
        })];
    }

    /**
     * Takes a string and splits it by 'new line' tags after all other tags have been parsed.
     * @param phaseString - the current phase string.
     * @param phasePrevious - The phase prior to this phase if this is not the first phase.
     */
    parsePhaseMulti = (phaseString: string, phasePrevious: Phase | null): string => {
        phaseString = this.stripTimingTags(phaseString, phasePrevious);
        phaseString = this.stripPhaseAlignment(phaseString);
        phaseString = this.stripImageTags(phaseString);

        let lineStrings = phaseString.split('[nl]');

        this.lines = [];

        lineStrings.forEach((lineString, index) => {
            this.lines.push(new Line({
                onChange: this.handleLineUpdate,
                maxWidth: this.maxWidth,
                font: this.font,
                index: index
            }));
        });

        let overflow: string = '';
        this.lines.forEach((line, index) => {
            let lineText = overflow + lineStrings[index];
            overflow = line.parseLineMulti(lineText);
        });

        return overflow;
    }

    /**
     * Gets the current timing tag.
     * @param phaseString - The current string being parsed by this phase.
     * @param phasePrevious - The phase prior to this phase if this is not the first phase.
     */
    stripTimingTags(phaseString: string, phasePrevious: Phase | null): string {
        let timingTags: RegExpMatchArray | null = phaseString.match(/\[pt([0-9]+)o([0-9]+)+\]/i);

        if (timingTags) {
            this.onTime = parseInt(timingTags[1]);
            this.offTime = parseInt(timingTags[2]);
        } else if (phasePrevious) {
            this.onTime = phasePrevious.onTime;
            this.offTime = phasePrevious.offTime;
        } else {
            this.onTime = 4;
            this.offTime = 1;
        }

        return phaseString.replace(/\[pt[0-9]+o[0-9]+\]/i, "");
    }

    /**
     * Gets the current phase alignment by using the jp tag.
     * @param phaseString - The current string being parsed by this phase.
     */
    stripPhaseAlignment(phaseString: string): string {
        let tag: RegExpMatchArray | null = phaseString.match(/\[jp([0-9])\]/i);

        if (tag) {
            this.alignment = parseInt(tag[1]) as PhaseAlignment;
        }

        return phaseString.replace(/\[jp[0-9]\]/i, "");
    }

    stripImageTags(phaseString: string): string {
        let tag: RegExpMatchArray | null = phaseString.match(/\[img([0-9]+)\]/i);

        if (tag) {
            this.graphicId = parseInt(tag[1]);
        }

        return phaseString.replace(/\[img[0-9]+\]/i, "");
    }

    handleLineUpdate = (update: ILineUpdate) => {

        let phaseUpdate: IPhaseUpdate = {
            phaseIndex: this.index
        };

        if (update.overflow && update.overflow.length > 0) {
            phaseUpdate = this.handleLineOverFlow(update);
        }
        else if (update.dequeuedChars && update.dequeuedChars.length > 0) {
            if (update.dequeuedChars === '\r') {
                //If a newline was deqeueued it was an empty line, remove that line
                this.lines.splice(update.lineIndex, 1)
            }
            else if (update.lineIndex > 0) {
                // Go to previous line and tell it to append then send another update.
                this.lines[update.lineIndex - 1].appendChars(update.dequeuedChars, true);
                return;
            } else {
                phaseUpdate.dequeuedChars = update.dequeuedChars;
            }
        }
        else if (update.underflow && update.underflow > 0) {
            phaseUpdate = this.handleLineUnderFlow(update);
        } else if (update.setHardLineBreak) {
            if (this.lines.length < this.lineCount) {
                this.lines.push(new Line({
                    onChange: this.handleLineUpdate,
                    maxWidth: this.maxWidth,
                    font: this.font,
                    index: this.lines.length
                }));
            } else {
                phaseUpdate.setHardPhaseBreak = true
            }
        }

        // update indicies incase they changed
        this.lines.forEach((line, index) => line.index = index);
        this.onChange(phaseUpdate);
    }

    /**
     * Handles when a new line overflows.
     * @param remainingChars - the characters remaining from the overflow
     * @param index - the index of the line that overflowed.
     */
    handleLineOverFlow = (update: ILineUpdate): IPhaseUpdate => {
        let index = update.lineIndex + 1;
        let overflow: string | null = update.overflow!;
        while (overflow && index < this.lineCount) {
            if (!this.lines[index]) {
                this.lines.push(new Line({
                    onChange: this.handleLineUpdate,
                    maxWidth: this.maxWidth,
                    font: this.font,
                    index: index
                }));
            }

            overflow = this.lines[index].prependChars(overflow);
            index++;
        }

        return {
            phaseIndex: this.index,
            overflow
        }
    }

    /**
     * Handles when a line under-flows.
     * @param requestedChars - the number of requested characters to underflow
     * @param index - the line requesting the characters
     */
    handleLineUnderFlow = (update: ILineUpdate): IPhaseUpdate => {
        let index = update.lineIndex + 1;
        let underflow: number | null = update.underflow!;
        while (underflow && index < this.lines.length) {
            let chars = this.lines[index].dequeueChars(underflow);
            underflow = underflow === chars.length ? underflow : null;
            this.lines[index - 1].appendChars(chars);
            index++;
        }

        return {
            phaseIndex: this.index,
            underflow
        }
    }

    /**
     * Called to add new characters to the end of an existing line that can
     * fit the extra chars.
     * @param newValue - The new char(s) to add to the end of this line
     */
    appendChars = (chars: string, updateMessage: boolean = false): string => {
        let overflow = '';
        let startIndex = this.lines.length - 1;
        let index = startIndex;

        while (index == startIndex || (overflow.length > 0 && index < this.lineCount)) {
            if (!this.lines[index]) {
                this.lines.push(new Line({
                    onChange: this.handleLineUpdate,
                    maxWidth: this.maxWidth,
                    font: this.font,
                    index: index
                }));
            }

            overflow = this.lines[index].appendChars(chars)
            chars = overflow
            index++;
        }

        if (updateMessage) {
            this.onChange({
                phaseIndex: this.index,
                overflow: overflow
            })
        }

        return overflow;
    }

    /**
     * Used to add new characters to the beginning of a new line.
     * @param newValue - The new char(s) to add to the beginning of this line
     */
    prependChars = (chars: string, updateMessage: boolean = false): string => {
        let overflow = '';
        let index = 0;

        while (index == 0 || (overflow.length > 0 && index < this.lineCount)) {
            if (!this.lines[index]) {
                this.lines.push(new Line({
                    onChange: this.handleLineUpdate,
                    maxWidth: this.maxWidth,
                    font: this.font,
                    index: index
                }));
            }

            overflow = this.lines[index].prependChars(chars)
            chars = overflow
            index++;
        }

        if (updateMessage) {
            this.onChange({
                phaseIndex: this.index,
                overflow: overflow
            })
        }

        return overflow;
    }

    dequeueChars = (numberOfChars: number): string => {
        let returnChars = '';
        let lineChars = '';
        let index = 0;

        while (index == 0 || (lineChars.length > 0 && index < this.lineCount)) {
            if (!this.lines[index]) {
                this.lines.push(new Line({
                    onChange: this.handleLineUpdate,
                    maxWidth: this.maxWidth,
                    font: this.font,
                    index: index
                }));
            }

            lineChars = this.lines[index].dequeueChars(numberOfChars)

            if (index > 0) {
                this.lines[index - 1].appendChars(lineChars);
            } else {
                returnChars = lineChars;
            }

            index++;
        }

        return returnChars;
    }

    /**
     * Takes all extra chars from previous phase and appends to this one.
     * @param value - prepends extra characters to this phase.
     */
    prepend = (value: string): string => {
        let index = 0;

        while (value && index < this.lineCount) {
            if (!this.lines[index]) {
                this.lines.push(new Line({
                    onChange: this.handleLineUpdate,
                    maxWidth: this.maxWidth,
                    font: this.font,
                    index: index
                }));
            }

            value = this.lines[index].prependChars(value);
            index++;
        }

        return value;
    }

    setLineAlignment = (alignment: LineAlignment) => {
        this.lines.forEach(line => line.alignment = alignment);
        this.onChange({
            phaseIndex: this.index
        })
    }

    toStrings = (): string[] => {
        return this.lines.map(line => line.toString());
    }

    toMultiText = (): string => {
        let lineMulti = this.lines.map(line => line.toMultiText()).join('[nl]');
        let phaseTimingTag = this.onTime === -1 ? '' : `[pt${this.onTime}o${this.offTime}]`
        return `${phaseTimingTag}${lineMulti}`;
    }
}